import React, { useState, useEffect } from 'react';
import { FiCalendar, FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import ScrollReveal from './ScrollReveal';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

export default function TodaysBlog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/public/blogs')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          // Sort by featured first, then by date descending
          const sorted = data.sort((a, b) => {
            if (a.is_featured !== b.is_featured) {
              return b.is_featured - a.is_featured;
            }
            return new Date(b.dated) - new Date(a.dated);
          });
          // Take top 6
          setBlogs(sorted.slice(0, 6));
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching blogs:', err);
        setLoading(false);
      });
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  if (loading) return null;
  if (blogs.length === 0) return null; // Hide section if no blog published

  return (
    <section className="todays-blog-section" style={{ padding: '80px 0 20px', backgroundColor: '#f8f9fa' }}>
      <div className="container">
        <ScrollReveal>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <span className="section-badge">Latest Updates</span>
            <h2 className="section-title">Recent Posts</h2>
            <div className="gold-divider" style={{ margin: '16px auto', width: '60px' }} />
          </div>
          
          <div className="recent-posts-wrapper">
            {/* Custom Navigation Buttons */}
            <div className="custom-swiper-prev">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            </div>
            <div className="custom-swiper-next">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </div>

            <Swiper
              key={blogs.length}
              modules={[Autoplay, Pagination, Navigation]}
              spaceBetween={30}
              slidesPerView={1}
              pagination={{ clickable: true }}
              navigation={{ prevEl: '.custom-swiper-prev', nextEl: '.custom-swiper-next' }}
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              loop={true}
              className="recent-posts-swiper"
            >
              {blogs.map(blog => (
                <SwiperSlide key={blog.id} style={{ height: 'auto' }}>
                  <div className="recent-post-card" style={{ 
                    backgroundColor: '#fff', 
                    borderRadius: '12px', 
                    overflow: 'hidden',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    display: 'flex',
                    flexDirection: 'row',
                    minHeight: '300px',
                    height: '100%'
                  }}>
                    <div style={{ padding: '40px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', gap: '16px', color: '#6c757d', fontSize: '0.9rem', marginBottom: '16px', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <FiCalendar size={14} /> {formatDate(blog.dated)}
                        </span>
                        {blog.is_featured === 1 && (
                          <span style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold' }}>
                            Featured
                          </span>
                        )}
                      </div>
                      <h3 style={{ fontSize: '1.6rem', color: '#198754', marginBottom: '16px', fontWeight: 'bold', lineHeight: '1.3' }}>
                        {blog.title}
                      </h3>
                      <p style={{ color: '#2d3446', lineHeight: '1.6', marginBottom: '24px', flex: 1 }}>
                        {blog.description.length > (blog.image_url ? 200 : 450) 
                          ? `${blog.description.substring(0, blog.image_url ? 200 : 450)}...` 
                          : blog.description}
                      </p>
                      <Link to={`/blogs/${blog.id}`} style={{ 
                        display: 'inline-flex', alignItems: 'center', gap: '8px', 
                        color: 'var(--primary)', fontWeight: 'bold', textDecoration: 'none',
                        marginTop: 'auto'
                      }}>
                        Read More <FiArrowRight />
                      </Link>
                    </div>
                    {blog.image_url && (
                      <div style={{ width: '350px', flexShrink: 0, borderLeft: '1px solid #f1f3f7' }}>
                        <img 
                          src={blog.image_url} 
                          alt={blog.title} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        />
                      </div>
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </ScrollReveal>
      </div>
      <style>{`
        .recent-posts-wrapper {
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
          padding: 0 60px;
        }
        .recent-posts-swiper {
          padding-bottom: 30px !important;
        }
        .custom-swiper-prev, .custom-swiper-next {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
          cursor: pointer;
          z-index: 10;
        }
        .custom-swiper-prev svg, .custom-swiper-next svg {
          width: 32px;
          height: 32px;
        }
        .custom-swiper-prev {
          left: 0;
        }
        .custom-swiper-next {
          right: 0;
        }
        .swiper-pagination-bullet-active { background: var(--primary) !important; }
        @media (max-width: 768px) {
          .recent-posts-wrapper {
            padding: 0 16px;
          }
          .custom-swiper-prev, .custom-swiper-next {
            display: none !important;
          }
          .recent-posts-swiper {
            padding-bottom: 50px !important;
          }
          .recent-post-card {
            flex-direction: column-reverse !important;
          }
          .recent-post-card > div:last-child {
            width: 100% !important;
            height: 250px;
            border-left: none !important;
            border-bottom: 1px solid #f1f3f7;
          }
          .recent-post-card > div:first-child {
            padding: 24px !important;
          }
        }
      `}</style>
    </section>
  );
}
